import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Card,
  CardHeader,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TextField,
  InputAdornment,
  Typography,
  MenuItem,
  Checkbox,
  Chip,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import shipmentService from "../../services/shipmentService";
import { selectStatusOptions } from "../../redux/slices/statusDefinitionsSlice";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import StatusChip from "../../components/common/StatusChip";
import { formatUsd } from "../../utils/currency";
import useUrlState from "../../hooks/useUrlState";
import useScrollRestoration from "../../hooks/useScrollRestoration";

const URL_SCHEMA = {
  search: { default: "" },
  statusFilter: { default: "" },
};

// Every item currently at "sailing" or any status after it, up to but not
// including "delivered" — arrived, customs, ready for pickup, scheduled for
// delivery — grouped Sailing → Shipment → Item, exactly the structure
// AdminSailingDetail.jsx uses for one sailing, just across every sailing at
// once. Delivered items are done, so they drop off this list for good.
// Lets an admin bulk-select a batch spanning several sailings/shipments and
// move them all to the same item status (including straight to Delivered)
// in one action, instead of one shipment at a time.
const AdminItemTracking = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const statusOptions = useSelector(selectStatusOptions("item"));

  // "sailing" onward through "delivered" is the relevant range for the
  // "move to" target — an admin can still move a batch straight to
  // Delivered from here.
  const eligibleStatusOptions = useMemo(() => {
    const idx = statusOptions.findIndex((s) => s.value === "sailing");
    return idx === -1 ? statusOptions : statusOptions.slice(idx);
  }, [statusOptions]);

  // The Status FILTER, though, excludes Delivered — the list itself never
  // includes delivered items (server drops them once they reach that
  // status), so filtering by it would always show nothing.
  const filterStatusOptions = useMemo(
    () => eligibleStatusOptions.filter((s) => s.value !== "delivered"),
    [eligibleStatusOptions],
  );

  useScrollRestoration();
  const [urlState, setUrlState] = useUrlState(URL_SCHEMA);
  const { statusFilter, search } = urlState;
  // Local typing buffer, debounced into urlState.search below — seeded
  // from the URL so a restored search still shows in the field.
  const [searchInput, setSearchInput] = useState(urlState.search);

  const [sailings, setSailings] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedItemIds, setSelectedItemIds] = useState(new Set());
  const [targetStatus, setTargetStatus] = useState("");
  const [applying, setApplying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await shipmentService.listItemsBySailing({
        search: search || undefined,
        status: statusFilter || undefined,
      });
      setSailings(data.data.sailings || []);
    } catch (err) {
      enqueueSnackbar("Failed to load items", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchInput === search) return;
    const timer = setTimeout(() => {
      setUrlState({ search: searchInput });
    }, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  useEffect(() => {
    load();
    setSelectedItemIds(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  const totalItemCount = sailings.reduce((sum, s) => sum + s.itemCount, 0);

  const toggleItemSelected = (itemId) => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const toggleShipmentItemsSelected = (shipmentGroup) => {
    const allSelected = shipmentGroup.items.every((it) => selectedItemIds.has(it.itemId));
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      shipmentGroup.items.forEach((it) => {
        if (allSelected) next.delete(it.itemId);
        else next.add(it.itemId);
      });
      return next;
    });
  };

  const handleApplyStatus = async () => {
    if (!targetStatus || selectedItemIds.size === 0) return;
    setApplying(true);
    try {
      const { data } = await shipmentService.bulkUpdateTrackingStatus(
        undefined,
        targetStatus,
        [...selectedItemIds],
      );
      const skipNotes = [];
      if (data.data.skippedNoInvoiceDoc?.length) {
        skipNotes.push(`${data.data.skippedNoInvoiceDoc.length} skipped (no invoice document)`);
      }
      enqueueSnackbar(
        `Updated ${data.data.updatedCount} item(s)${skipNotes.length ? ` — ${skipNotes.join(", ")}` : ""}`,
        { variant: data.data.updatedCount > 0 ? "success" : "warning" },
      );
      setSelectedItemIds(new Set());
      setTargetStatus("");
      load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to update item status", {
        variant: "error",
      });
    } finally {
      setApplying(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Item Tracking"
        subtitle="Every item at Sailing status or later (excluding Delivered), grouped by sailing and shipment — bulk-select a batch and move them all to the same status at once"
      />

      <Card>
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
          }}
        >
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <TextField
              size="small"
              placeholder="Search tracking #, description, shipment…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              sx={{ width: 280 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 16, color: "text.disabled" }} />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              size="small"
              select
              label="Status"
              value={statusFilter}
              onChange={(e) => setUrlState({ statusFilter: e.target.value })}
              sx={{ width: 220 }}
            >
              <MenuItem value="">Sailing or later (all)</MenuItem>
              {filterStatusOptions.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {totalItemCount} item(s) across {sailings.length} sailing(s)
          </Typography>
        </Box>

        {selectedItemIds.size > 0 && (
          <Box
            sx={{
              p: 1.5,
              px: 2,
              bgcolor: "primary.50",
              borderBottom: "1px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              {selectedItemIds.size} selected
            </Typography>
            <TextField
              size="small"
              select
              label="Move to status"
              value={targetStatus}
              onChange={(e) => setTargetStatus(e.target.value)}
              sx={{ width: 220 }}
            >
              {eligibleStatusOptions.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              size="small"
              disabled={!targetStatus || applying}
              onClick={handleApplyStatus}
            >
              {applying ? "Applying…" : `Apply to ${selectedItemIds.size} item(s)`}
            </Button>
            <Button size="small" onClick={() => setSelectedItemIds(new Set())}>
              Clear selection
            </Button>
          </Box>
        )}
      </Card>

      {loading ? (
        <SkeletonLoader rows={8} />
      ) : sailings.length === 0 ? (
        <EmptyState icon="⛵" title="No items found" description="Try adjusting your filters or search." />
      ) : (
        sailings.map((sailing) => (
          <Card key={sailing.sailingId}>
            <CardHeader
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                  <Typography fontWeight={700} fontSize={15}>
                    {sailing.vesselName}
                    {sailing.voyageNumber ? ` / ${sailing.voyageNumber}` : ""}
                  </Typography>
                  <StatusChip status={sailing.status} domain="sailing" />
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                    #{sailing.sailingNumber}
                  </Typography>
                  <Chip label={`${sailing.itemCount} item(s)`} size="small" />
                </Box>
              }
              subheader={
                <Typography variant="caption" color="text.secondary">
                  Departs {dayjs(sailing.departureDate).format("MMM D, YYYY")} · ETA{" "}
                  {dayjs(sailing.estimatedArrivalDate).format("MMM D, YYYY")}
                </Typography>
              }
              action={
                <Button size="small" variant="outlined" onClick={() => navigate(`/admin/sailing/${sailing.sailingId}`)}>
                  View Sailing
                </Button>
              }
              sx={{ pb: 0 }}
            />
            <Box sx={{ overflowX: "auto", mt: 1.5 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>Item</TableCell>
                    <TableCell>Item Tracking #</TableCell>
                    <TableCell>D/R</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Item Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sailing.shipments.map((group) => {
                    const allSelected = group.items.every((it) => selectedItemIds.has(it.itemId));
                    const someSelected = group.items.some((it) => selectedItemIds.has(it.itemId));
                    return (
                      <React.Fragment key={group.shipmentId}>
                        <TableRow
                          hover
                          sx={{
                            cursor: "pointer",
                            bgcolor: "#001A3C",
                            "&.MuiTableRow-hover:hover": { bgcolor: "#012452" },
                          }}
                        >
                          <TableCell padding="checkbox" sx={{ borderBottom: "none" }}>
                            <Checkbox
                              checked={allSelected}
                              indeterminate={someSelected && !allSelected}
                              onChange={() => toggleShipmentItemsSelected(group)}
                              onClick={(e) => e.stopPropagation()}
                              sx={{ color: "rgba(255,255,255,0.7)" }}
                            />
                          </TableCell>
                          <TableCell
                            colSpan={5}
                            sx={{ borderBottom: "none" }}
                            onClick={() => navigate(`/admin/shipments/${group.shipmentTrackingNumber}`)}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                              <Typography
                                variant="body2"
                                fontWeight={800}
                                sx={{ fontFamily: "monospace", color: "white" }}
                              >
                                {group.shipmentTrackingNumber}
                              </Typography>
                              <Typography variant="caption" fontWeight={700} sx={{ color: "rgba(255,255,255,0.75)" }}>
                                {group.customer?.firstName} {group.customer?.lastName}
                              </Typography>
                              <Chip
                                label={`${group.items.length} item(s)`}
                                size="small"
                                sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "white", fontWeight: 700 }}
                              />
                            </Box>
                          </TableCell>
                        </TableRow>

                        {group.items.map((item) => (
                          <TableRow key={item.itemId} hover selected={selectedItemIds.has(item.itemId)}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedItemIds.has(item.itemId)}
                                onChange={() => toggleItemSelected(item.itemId)}
                              />
                            </TableCell>
                            <TableCell sx={{ pl: 1 }}>
                              <Typography variant="body2" fontWeight={600} noWrap maxWidth={220}>
                                {item.description || "—"}
                              </Typography>
                              {item.consolidation && (
                                <Typography variant="caption" color="text.secondary">
                                  In consolidation
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell
                              sx={{ fontFamily: "monospace", fontSize: 12, color: "text.secondary", whiteSpace: "nowrap" }}
                            >
                              {item.trackingNumber || "—"}
                            </TableCell>
                            <TableCell sx={{ fontFamily: "monospace", fontSize: 12, color: "text.secondary" }}>
                              {item.dockReceiptNumber || "—"}
                            </TableCell>
                            <TableCell>
                              <StatusChip status={item.itemStatus} domain="item" />
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              {formatUsd(item.totalValue)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Card>
        ))
      )}
    </Box>
  );
};

export default AdminItemTracking;
